import { DEFAULT_TIMEZONE, consoleRoutes } from "@sagot/constants";
import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import {
  AppLink,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Icon,
  Skeleton,
  toast,
} from "@sagot/ui";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions, useRouteAgentId } from "../../../session/permissions";
import { useWorkspaceContext, useWorkspaceDetails } from "../../../session/workspace-context";
import { AgentStatusBadge } from "../shared/agent-status";
import { downloadFile } from "../shared/download";
import { PageHeader } from "../shared/page-header";
import { useAgentConfig, useAgentList } from "../shared/queries";
import { TypedConfirmDialog } from "../shared/typed-confirm-dialog";
import type { AgentConfig } from "../shared/types";
import { SettingsForm } from "./settings-form";
import { isTab, type SettingsTab } from "./settings-model";
import { useSettingsMutations } from "./use-agent-settings";
import { VersionsDialog } from "./versions-dialog";

const NOT_FOUND = 404;

function useCan(agentId: string) {
  const permissions = usePermissions(agentId);
  const writable = !permissions.readOnly;
  return {
    edit: writable && permissions.can("agents.edit", { agentId }),
    publish: writable && permissions.can("agents.publish", { agentId }),
    create: writable && permissions.can("agents.create", { anywhere: true }),
    remove: writable && permissions.can("agents.delete", { agentId }),
    readOnly: permissions.readOnly,
    pending: permissions.isPending,
  };
}

function HeaderMenu({ config, onHistory, canCreate }: { config: AgentConfig; onHistory: () => void; canCreate: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const { duplicate } = useSettingsMutations(config.id);
  const exportJson = () => {
    const file = `${config.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "agent"}.json`;
    downloadFile(file, JSON.stringify(config, null, 2), "application/json");
    toast.success(t("build.agentSettings.exported", { file }));
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" iconOnly aria-label={t("build.agentSettings.more")}>
          <Icon name="more-horizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {canCreate ? (
          <DropdownMenuItem
            onSelect={() => {
              duplicate.mutate(undefined, {
                onSuccess: (copy) => { toast.success(t("build.agentSettings.duplicated", { name: copy.name })); void navigate(consoleRoutes.agentSettings(summary.slug, copy.id)); },
                onError: (error) => { toast.error(message(error)); },
              });
            }}
          >
            <Icon name="copy" />
            {t("build.agentSettings.duplicate")}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={exportJson}>
          <Icon name="download" />
          {t("build.agentSettings.export")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onHistory}>
          <Icon name="history" />
          {t("build.agentSettings.history")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SettingsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]" aria-hidden="true">
      <div className="space-y-2">{Array.from({ length: 9 }, (_, i) => <Skeleton key={i} className="h-9" />)}</div>
      <div className="space-y-6"><Skeleton className="h-72 rounded-card" /><Skeleton className="h-48 rounded-card" /></div>
    </div>
  );
}

export function AgentSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const agentId = useRouteAgentId() ?? "";
  const params = useParams();
  const tab: SettingsTab = isTab(params.tab) ? params.tab : "general";
  const config = useAgentConfig(agentId);
  const list = useAgentList();
  const timezone = useWorkspaceDetails().data?.timezone ?? DEFAULT_TIMEZONE;
  const can = useCan(agentId);
  const { update, remove } = useSettingsMutations(agentId);
  const [history, setHistory] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const sourceCount = list.data?.items.find((a) => a.id === agentId)?.stats.sources_count ?? 0;

  if (config.isPending || can.pending) return <div className="mx-auto max-w-7xl space-y-6"><Skeleton className="h-14 w-96" /><SettingsSkeleton /></div>;
  if (config.isError) {
    const missing = isApiError(config.error) && config.error.status === NOT_FOUND;
    return missing ? (
      <EmptyState icon="bot" title={t("build.noAgent")} description={t("build.noAgentBody")} action={<Button asChild><AppLink href={consoleRoutes.agents(summary.slug)}>{t("build.goToAgents")}</AppLink></Button>} />
    ) : (
      <ErrorState title={t("build.loadError")} description={t("build.loadErrorBody")} onRetry={() => void config.refetch()} retryLabel={t("common.retry")} />
    );
  }
  const agent = config.data;
  const toggleStatus = () => {
    const status = agent.status === "live" ? "paused" : "live";
    update.mutate({ status }, {
      onSuccess: (next) => {
        toast[next.status === "live" ? "success" : "warning"](t(next.status === "live" ? "build.agentSettings.danger.resumed" : "build.agentSettings.danger.paused", { name: next.name }));
        setFormKey((k) => k + 1);
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title={<span className="flex flex-wrap items-center gap-2">{t("build.agentSettings.title", { name: agent.name })}<AgentStatusBadge status={agent.status} /></span>}
        subtitle={t("build.agentSettings.subtitle")}
        actions={
          <>
            <Button asChild variant="secondary">
              <AppLink href={consoleRoutes.playground(summary.slug, agentId)}>
                <Icon name="messages-square" />
                {t("build.agentSettings.testInPlayground")}
              </AppLink>
            </Button>
            <HeaderMenu config={agent} onHistory={() => { setHistory(true); }} canCreate={can.create} />
          </>
        }
      />
      <SettingsForm
        key={`${agent.id}:${String(formKey)}`}
        config={agent}
        tab={tab}
        timezone={timezone}
        sourceCount={sourceCount}
        can={can}
        dangerProps={{ status: agent.status, canToggle: agent.status === "live" ? can.edit : can.publish, canDelete: can.remove, toggling: update.isPending, onToggle: toggleStatus, onDelete: () => { setDeleting(true); } }}
      />
      <VersionsDialog agentId={agentId} open={history} onOpenChange={setHistory} canEdit={can.edit} canPublish={can.publish} onRestored={() => { setFormKey((k) => k + 1); }} />
      <TypedConfirmDialog
        open={deleting}
        onOpenChange={(open) => { setDeleting(open); if (!open) remove.reset(); }}
        title={t("build.agentSettings.danger.deleteTitle", { name: agent.name })}
        body={t("build.agentSettings.danger.deleteBody")}
        confirmLabel={t("build.agentSettings.danger.deleteConfirm", { name: agent.name })}
        expected={agent.name}
        submitLabel={t("build.agentSettings.danger.delete")}
        pending={remove.isPending}
        error={remove.isError ? message(remove.error) : null}
        onConfirm={(typed) => {
          remove.mutate(typed, { onSuccess: () => { toast.success(t("build.agentSettings.danger.deleted")); void navigate(consoleRoutes.agents(summary.slug)); } });
        }}
      />
    </div>
  );
}
